import { ATTRIBUTES, DURATIONS, DYNAMIC_ENGINE_REGIONS, LEGACY_REGIONNAMES, PLACES } from '../constants';

export { claimErrorReload, reloadAfterLogging } from './errorReload';
export { logError, logMessage } from './logger';

const findIdByKey = (name, collection, key) => collection.find((item) => item[key] === name)?.id;

const findShortNameByName = (name, collection, key) => collection.find((item) => item[key] === name)?.shortname;

const mapCollectionToIds = (attrs, collection, key) => {
  if (!Array.isArray(attrs)) {
    return findIdByKey(attrs, collection, key);
  }

  return attrs.map((attr) => findIdByKey(attr, collection, key)).filter(Boolean);
};

const mapCollectionToShortNames = (attrs, collection, key) => {
  if (!Array.isArray(attrs)) {
    return findShortNameByName(attrs, collection, key);
  }

  return attrs.map((attr) => findShortNameByName(attr, collection, key)).filter(Boolean);
};

export function mapAttributesToIds(attrs, key = 'name') {
  return mapCollectionToIds(attrs, ATTRIBUTES, key);
}

export function mapAttributesToShortNames(attrs, key = 'name') {
  return mapCollectionToShortNames(attrs, ATTRIBUTES, key);
}

export function mapPlacesToIds(attrs, key = 'name') {
  return mapCollectionToIds(attrs, PLACES, key);
}

export function mapRegionsToIds(attrs, key = 'name') {
  return mapCollectionToIds(attrs, DYNAMIC_ENGINE_REGIONS, key);
}

export function mapRegionsToShortNames(attrs, key = 'name') {
  return mapCollectionToShortNames(attrs, DYNAMIC_ENGINE_REGIONS, key);
}

export function mapDurationToDays(duration) {
  // eslint-disable-next-line no-console
  // console.log('duration:', duration)
  const dur = mapCollectionToIds(duration, DURATIONS, 'name');
  return dur ? dur : parseInt(duration, 10);
}

export function GTMPageView(url) {
  gtag({
    event: 'pageview',
    page: url
  });
}

export function gtag() {
  if (!window) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push(arguments);
}

const readTracardiStorage = (key) => {
  try {
    return JSON.parse(globalThis.localStorage?.getItem(key) ?? 'null');
  } catch {
    return null;
  }
};

const hasMarketingConsent = () => globalThis.Cookiebot?.consent?.marketing === true;

// The tracker snippet sets __tracardiReady and drains this queue once Tracardi has replied to its
// page-view, which is when the visitor's profile id is stored.
function whenTracardiReady(send) {
  // Cookiebot can't stop a tracker that already loaded, and client-side navigation keeps it alive,
  // so a visitor who withdraws consent mid-session would otherwise still be tracked.
  if (globalThis.window == null || !hasMarketingConsent()) return;

  const win = globalThis.window;
  const run = () => {
    const tracker = win.tracardi?.default;
    if (!tracker || !hasMarketingConsent()) return;
    // Tracking is optional: a tracker failure must never reach the caller (e.g. checkout's redirect
    // to payment) or stop the snippet sending the rest of the queue.
    try {
      Promise.resolve(send(tracker)).catch(() => {});
    } catch {
      // Swallowed for the same reason.
    }
  };

  if (win.__tracardiReady) {
    run();
  } else {
    win.__tracardiQueue ??= [];
    win.__tracardiQueue.push(run);
  }
}

function postTrackPayload(tracker, eventName, eventData, userId) {
  const state = tracker.getState?.();
  const config = state?.plugins?.tracardi?.config?.tracker;
  const apiUrl = config?.url?.api;
  const sourceId = config?.source?.id;
  if (!apiUrl || !sourceId) return;

  const deviceId = readTracardiStorage('tracardi-device-id') ?? state?.user?.anonymousId;
  const profileId = readTracardiStorage('tracardi-profile-id');
  const sessionId = state?.context?.sessionId;
  const now = new Date().toISOString();

  const payload = {
    source: { id: sourceId },
    context: {
      time: {
        local: now,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        offset: new Date().getTimezoneOffset()
      },
      referer: { host: null, query: null },
      browser: {
        local: {
          browser: {
            userAgent: globalThis.navigator.userAgent,
            language: globalThis.navigator.language,
            onLine: globalThis.navigator.onLine,
            cookieEnabled: globalThis.navigator.cookieEnabled
          }
        }
      }
    },
    profile: profileId ? { id: profileId } : null,
    device: deviceId ? { id: deviceId } : null,
    session: sessionId ? { id: sessionId } : null,
    options: {},
    events: [
      {
        time: { create: now },
        type: eventName,
        user_id: userId,
        properties: eventData,
        options: {},
        context: {
          page: {
            url: globalThis.location?.href,
            path: globalThis.location?.pathname,
            title: globalThis.document.title
          }
        }
      }
    ]
  };

  fetch(`${apiUrl}/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-timestamp': Date.now().toString() },
    body: JSON.stringify(payload),
    keepalive: true
  })
    .then((res) => res.json())
    .then((data) => {
      const returnedProfileId = data?.profile?.id;
      if (returnedProfileId) {
        globalThis.localStorage?.setItem('tracardi-profile-id', JSON.stringify(returnedProfileId));
      }
    })
    .catch(() => {});
}

export function trackTracardi(eventName, eventData = {}, options = {}) {
  whenTracardiReady((tracker) => {
    // The tracker can't put user_id on the event itself, which checkout-started sends.
    if (options.userId) {
      postTrackPayload(tracker, eventName, eventData, options.userId);
      return;
    }
    // Without fire the tracker holds the event, as it only sends one batch per page load. Not the plugin
    // helper: it console.logs every payload, which Sentry keeps as breadcrumbs.
    return tracker.track(eventName, eventData, options.asBeacon ? { fire: true, asBeacon: true } : { fire: true });
  });
}

// Tracardi can resolve identity on the email, linking this browser's anonymous history to that person.
export function trackEmailSubmitted(email, formType, { marketingOptOut = false } = {}) {
  whenTracardiReady((tracker) => {
    // Not the plugin helper: it console.logs its payload, which would put the email in Sentry breadcrumbs.
    return tracker.track(
      'email-submitted',
      { email: email.trim(), form_type: formType, marketing_opt_out: Boolean(marketingOptOut) },
      { fire: true }
    );
  });
}

/**
 * @param {{ blueprintId: string, title: string, tags?: { tagId: string, tagText: string | null }[], sectionTag?: string, url?: string, virtual?: boolean }} blueprint
 */
export function trackBlueprintClicked({ blueprintId, title, tags = [], sectionTag, url, virtual = false }) {
  // Every blueprint click is a full page load, which cancels a normal request; a beacon survives it.
  trackTracardi(
    'blueprint-clicked',
    {
      blueprint_id: blueprintId,
      blueprint_title: title,
      tags: tags.map(({ tagId, tagText }) => ({ tag_id: tagId, tag_text: tagText })),
      ...(sectionTag ? { section_tag: sectionTag } : {}),
      ...(url ? { blueprint_url: url } : {}),
      ...(virtual ? { virtual: true } : {})
    },
    { asBeacon: true }
  );
}

// type TrackEventArgs = {
//   eventName: string
//   eventData: Record<string, any>
// }

export const trackEvent = (eventName, eventData) => {
  const { event_category, event_label, ...rest } = eventData;

  gtag('event', eventName, {
    event_category,
    event_label,
    ...rest
  });
};

export function base64Encode(input) {
  // create a buffer
  const buff = Buffer.from(input, 'utf8');

  // encode buffer as Base64
  const encoded = buff.toString('base64');
  return encoded;
}

export function replaceLast(source, find, replace) {
  var index = source.lastIndexOf(find);

  if (index >= 0) {
    return source.substring(0, index) + replace + source.substring(index + find.length);
  } else return source;
}

export function mapLegacyRegionNameToId(regionName) {
  return LEGACY_REGIONNAMES.find((region) => region.legacyname === regionName)?.id;
}

export function stripHtml(html) {
  const strippedHtml = html?.replace(/<[^>]+>/g, '');
  return strippedHtml ?? '';
}

export function isDev() {
  return ['development', 'dev', 'test'].includes(process.env.NODE_ENV);
}

export function isProd() {
  return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'prod';
}

export function isVercelPreview() {
  return process.env.VERCEL_ENV === 'preview' || process.env.VERCEL_ENV === 'development';
}

export function removePii(person) {
  const pii = ['first_name', 'last_name', 'email_address', 'phone_number'];
  for (let i = 0; i < pii.length; i++) {
    delete person[pii[i]];
  }
}

export function slugify(text) {
  const slug = text
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug;
}
